// One contract as an administrator sees it: status, documents (each viewable,
// with its signed copy and Word source), who has signed, warnings, and the
// timeline. Actions are the caller's — passed in as `actions` — because what may
// be done differs between an organiser's agreement and an engagement schedule.
import { useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Stack, Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PdfPreview, { followDownload } from "./PdfPreview";
import { API_BASE_URL } from "../config";
import { CONTRACT_STATUS, contractLinkUrl, fmtDateTime, signatureFor } from "../contracts";

// A short-lived named link to one of a document's files ({url, downloadUrl}) —
// what PdfPreview loads, so every way of saving a file gives the server's name
// for it (see PdfPreview).
export const fetchContractLink = (contract, doc, version) =>
  axios.get(contractLinkUrl(contract, doc), { params: { version } }).then(r => ({
    url: API_BASE_URL + r.data.url, downloadUrl: API_BASE_URL + r.data.download_url,
  }));

export const downloadContractFile = (contract, doc, version) =>
  fetchContractLink(contract, doc, version).then(l => followDownload(l.downloadUrl)).catch(() => {});

/** A document viewed in a dialog over whatever opened it. */
export function ContractDocumentDialog({ contract, doc, version = "current", onClose }) {
  return (
    <Dialog open={!!doc} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{doc?.title}{version === "signed" ? " — signed copy" : ""}</DialogTitle>
      <DialogContent dividers>
        {doc && (
          <PdfPreview title={doc.title} reloadKey={`${contract.id}-${doc.id}-${version}-${doc.current_sha256}`}
                      load={() => fetchContractLink(contract, doc, version)} />
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function SignatureLine({ label, sig }) {
  if (!sig) {
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        {label}: not signed yet
      </Typography>
    );
  }
  return (
    <Typography variant="caption" component="div" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <TaskAltIcon sx={{ fontSize: 14, color: "success.main" }} />
      {label}: {sig.signer_name}, {fmtDateTime(sig.signed_at)} ({sig.method === "typed" ? "typed" : "drawn"})
    </Typography>
  );
}

/**
 * Props: contract (ContractRead), counterpartyLabel ("Organiser" | "Contractor"),
 * actions (node), showSource (bool — the Word files), defaultHistoryOpen.
 */
export default function ContractSummary({ contract, counterpartyLabel = "Counterparty", actions,
                                          showSource = true, defaultHistoryOpen = false }) {
  const [viewing, setViewing] = useState(null); // {doc, version}
  const [historyOpen, setHistoryOpen] = useState(defaultHistoryOpen);
  const status = CONTRACT_STATUS[contract.status] || { label: contract.status, color: "default" };
  const noPlaceholders = contract.documents.some(d =>
    d.source_filename && !d.signature_fields.some(f => f.startsWith("ShelterStone")));

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={status.label} color={status.color} />
          {contract.agreement_label && (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{contract.agreement_label}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {counterpartyLabel}: {contract.signer_name || "—"}
          {contract.signer_email ? ` (${contract.signer_email})` : ""}
          {contract.sent_at ? ` · sent ${fmtDateTime(contract.sent_at)}` : ""}
          {contract.completed_at ? ` · completed ${fmtDateTime(contract.completed_at)}` : ""}
          {contract.status === "sent" && contract.link_expires_at
            ? ` · link valid until ${fmtDateTime(contract.link_expires_at)}` : ""}
          {contract.status === "voided"
            ? ` · voided${contract.voided_by_name ? ` by ${contract.voided_by_name}` : ""}${contract.void_reason ? `: ${contract.void_reason}` : ""}`
            : ""}
        </Typography>

        {contract.finalise_error && (
          <Alert severity="error">
            Everyone has signed, but producing the signed {contract.documents.length > 1 ? "copies" : "copy"} failed:
            {" "}{contract.finalise_error}
          </Alert>
        )}
        {contract.stale_fields?.length > 0 && (
          <Alert severity="warning">
            Changed on the record since this was prepared: {contract.stale_fields.join(", ")}.
            Void it and send a new one to include the change.
          </Alert>
        )}
        {contract.status === "draft" && contract.blank_fields?.length > 0 && (
          <Alert severity="warning">Left blank (nothing on the record): {contract.blank_fields.join(", ")}.</Alert>
        )}
        {contract.status === "draft" && noPlaceholders && (
          <Alert severity="info">
            A document has no signature placeholders, so its signatures will appear only on the
            signing certificate added at the end.
          </Alert>
        )}

        {contract.documents.map(doc => (
          <Box key={doc.id} sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 160 }}>
                {doc.title}
                {doc.source_filename && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {" "}— {doc.source_filename}
                  </Typography>
                )}
              </Typography>
              {doc.current_sha256 && contract.status !== "completed" && (
                <Button size="small" startIcon={<PictureAsPdfIcon />}
                        onClick={() => setViewing({ doc, version: "current" })}>View</Button>
              )}
              {doc.has_signed_copy && (
                <>
                  <Button size="small" startIcon={<PictureAsPdfIcon />}
                          onClick={() => setViewing({ doc, version: "signed" })}>Signed copy</Button>
                  <Button size="small" onClick={() => downloadContractFile(contract, doc, "signed")}>
                    Download
                  </Button>
                </>
              )}
              {showSource && doc.source_filename && (
                <Button size="small" startIcon={<DescriptionIcon />}
                        onClick={() => downloadContractFile(contract, doc, "source")}>Word</Button>
              )}
            </Box>
            {doc.unknown_fields?.length > 0 && (
              <Typography variant="caption" color="warning.main" component="div">
                Placeholders nothing fills (they'll print blank): {doc.unknown_fields.map(f => `{{${f}}}`).join(", ")}
              </Typography>
            )}
            {contract.status !== "draft" && (
              <>
                <SignatureLine label="Shelter Stone" sig={signatureFor(doc, "shelter_stone")} />
                <SignatureLine label={counterpartyLabel} sig={signatureFor(doc, "counterparty")} />
              </>
            )}
          </Box>
        ))}

        {actions && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box>}

        {contract.history?.length > 0 && (
          <Box>
            <Button size="small" onClick={() => setHistoryOpen(o => !o)}
                    endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
              History ({contract.history.length})
            </Button>
            <Collapse in={historyOpen} unmountOnExit>
              <Stack spacing={0.5} sx={{ pl: 1, pt: 0.5 }}>
                {[...contract.history].reverse().map((h, i) => (
                  <Box key={i}>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}{h.ip ? ` · ${h.ip}` : ""}
                    </Typography>
                    <Typography variant="body2">{h.summary}</Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
      </Stack>
      <ContractDocumentDialog contract={contract} doc={viewing?.doc} version={viewing?.version}
                              onClose={() => setViewing(null)} />
    </Paper>
  );
}
